import { AxiosError } from "axios";
import { parseISO } from "date-fns";
import { useContext } from "react";
import { useQuery, UseQueryResult, QueryFunctionContext } from "react-query";
import { log } from "common/Logger";
import { useErrorLogger } from "common/LoggerHooks";
import {
  Antrag,
  DefenderDokumentStatus,
  DefenderUploadedDokument,
  DokumentStatusResult,
  GastAntrag,
  InstallationsUnternehmen,
} from "interfaces/Application";
import { ExpertSkill } from "interfaces/ExpertSkill";
import { Gemeinde, Konzessionsgebiet, Sparte } from "interfaces/Municipal";
import { ApiContext } from "services/ApiContext";
import { NIL as EmptyGuid } from "uuid";

const defaultLookupStaleTime = 15 * 60 * 1000; /* 15 Minuten, Default: 0 Minuten */
const statusLookupStaleTime = .25 * 60 * 1000; /* 15 Sekunden, Default: 0 Minuten */
const defaultLookupCacheTime = 5 * 60 * 1000; /* Default: 5 Minuten */

export const useApplicationsQueries = (): UseQueryResult<Antrag[], AxiosError> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);

  const GetApplications = async (): Promise<Antrag[]> => {
    const response = await api.get("/antraege");
    log.trace({ obj: response.data }, "result from get applications");
    return response.data.map((d) => {
      d["gewerbeAngemeldetAm"] = d["gewerbeAngemeldetAm"] ? parseISO(d["gewerbeAngemeldetAm"]) : new Date();
      d["antragsDatum"] = d["antragsDatum"] ? parseISO(d["antragsDatum"]) : new Date();
      return d;
    });
  };
  return useQuery<Antrag[], AxiosError>({
    queryKey: "Applications",
    queryFn: GetApplications,
    onError: (err) => logAxiosError("Error loading Anträge", "Fehler beim Laden der Anträge", err),
  });
};

export const useGuestApplicationsQueries = (): UseQueryResult<GastAntrag[], AxiosError> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);

  const GetGuestApplications = async (): Promise<GastAntrag[]> => {
    const response = await api.get("/gastantraege");
    log.trace({ obj: response.data }, "result from get Guest applications");
    return response.data.map((d) => {
      d["ausweisGueltigkeit"] = d["ausweisGueltigkeit"] ? parseISO(d["ausweisGueltigkeit"]) : new Date();
      return d;
    });
  };
  return useQuery<GastAntrag[], AxiosError>({
    queryKey: "GuestApplications",
    queryFn: GetGuestApplications,
    onError: (err) => logAxiosError("Error loading GuestAnträge", "Fehler beim Laden der GuestAnträge", err),
  });
};

export const useSkillsQueries = (): UseQueryResult<ExpertSkill[], AxiosError> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);

  const GetExpertSkills = async (): Promise<ExpertSkill[]> => {
    const response = await api.get<ExpertSkill[]>("/fachmannbefaehigungen");
    log.trace({ obj: response.data }, "result from get ExpertSkills");
    return response.data;
  };
  return useQuery<ExpertSkill[], AxiosError>({
    queryKey: "ExpertSkill",
    queryFn: GetExpertSkills,
    staleTime: defaultLookupStaleTime,
    cacheTime: defaultLookupCacheTime,
    onError: (err) => logAxiosError("Error loading Befähigungen", "Fehler beim Laden der Befähigungen", err),
  });
};

export const useKonzessionsgebieteQueries = (): UseQueryResult<Konzessionsgebiet[], AxiosError> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);

  const GetKonzessionsgebiete = async (): Promise<Konzessionsgebiet[]> => {
    const response = await api.get<Konzessionsgebiet[]>("/konzessionen/gebiete");
    log.trace({ obj: response.data }, "result from get konzessionsgebiete");
    return response.data;
  };
  return useQuery<Konzessionsgebiet[], AxiosError>({
    queryKey: "Konzessionsgebiet",
    queryFn: GetKonzessionsgebiete,
    staleTime: defaultLookupStaleTime,
    cacheTime: defaultLookupCacheTime,
    onError: (err) =>
      logAxiosError("Error loading Konzessionsgebiete", "Fehler beim Laden der Konzessionsgebiete", err),
  });
};

export const useGemeindenQueries = (): UseQueryResult<Gemeinde[], AxiosError> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);

  const GetGemeinden = async (): Promise<Gemeinde[]> => {
    const response = await api.get<Gemeinde[]>("/gemeinden");
    log.trace({ obj: response.data }, "result from get Gemeinden");
    return response.data;
  };
  return useQuery<Gemeinde[], AxiosError>({
    queryKey: "Gemeinden",
    queryFn: GetGemeinden,
    staleTime: defaultLookupStaleTime,
    cacheTime: defaultLookupCacheTime,
    onError: (err) => logAxiosError("Error loading Gemeinden", "Fehler beim Laden der Gemeinden", err),
  });
};

export const useCompaniesQueries = (): UseQueryResult<InstallationsUnternehmen[], AxiosError> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);

  const GetCompanies = async (): Promise<InstallationsUnternehmen[]> => {
    const response = await api.get<InstallationsUnternehmen[]>("/installationsunternehmen");
    log.trace({ obj: response.data }, "result from get Companies");
    return response.data;
  };
  return useQuery<InstallationsUnternehmen[], AxiosError>({
    queryKey: "Companies",
    queryFn: GetCompanies,
    staleTime: defaultLookupStaleTime,
    cacheTime: defaultLookupCacheTime,
    onError: (err) =>
      logAxiosError("Error loading installationsunternehmen", "Fehler beim Laden der installationsunternehmen", err),
  });
};

export const useSpartenQueries = (): UseQueryResult<Sparte[], AxiosError> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);

  const GetSparten = async (): Promise<Sparte[]> => {
    const response = await api.get<{ value: Array<Sparte> }>("/sparten");
    log.trace({ obj: response.data }, "result from get sparten");
    return response.data.value;
  };
  return useQuery<Sparte[], AxiosError>({
    queryKey: "Sparten",
    queryFn: GetSparten,
    staleTime: defaultLookupStaleTime,
    cacheTime: defaultLookupCacheTime,
    onError: (err) => logAxiosError("Error loading Sparten", "Fehler beim Laden der Sparten", err),
  });
};

type UploadDocQueryKey = readonly [
  key: string,
  queryKey: {
    docs: DefenderUploadedDokument[];
    installationsUnternehmenId: string;
  }
];

export const useDokumentStatusQueries = (
  docs: DefenderUploadedDokument[],
  installationsUnternehmenId: string
): UseQueryResult<DefenderUploadedDokument[], AxiosError> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);

  const GetDokumentStatus = async ({ queryKey }: QueryFunctionContext<UploadDocQueryKey>) => {
    const [, { docs, installationsUnternehmenId }] = queryKey;
    // installationsUnternehmenId muss für Statusabfrage gesetzt sein
    docs.forEach(doc => {
      if (doc.installationsUnternehmenId !== installationsUnternehmenId) doc.installationsUnternehmenId = installationsUnternehmenId
      if (doc.installationsUnternehmenId === "") doc.installationsUnternehmenId = EmptyGuid;
    })
    const filteredDocs = docs.filter(d => d.dateiName !== "" && d.documentId !== "" && d.subject !== "" && d.status === DefenderDokumentStatus.NotChecked);
    const response = await api.put<DokumentStatusResult[]>("/dokumente/status", filteredDocs);
    log.trace({ obj: response.data }, "result from get DokumentStatus");
    if (response.data) {
      const updatedDocs = docs.map((doc) => {
        const docStatusData = response.data.find((x) => x.documentId === doc.documentId);
        if (docStatusData !== undefined) {
          return { ...doc, status: docStatusData.status };
        } else return doc;
      });
      return updatedDocs;
    }
    log.warn("No or empty data array provided, returning empty array");
    return [];
  };
  return useQuery<DefenderUploadedDokument[], AxiosError, DefenderUploadedDokument[], UploadDocQueryKey>({
    queryKey: ["docStatus", { docs, installationsUnternehmenId }],
    queryFn: GetDokumentStatus,
    staleTime: statusLookupStaleTime,
    cacheTime: defaultLookupCacheTime,
    onError: (err) => logAxiosError("Error loading DokumentStatus", "Fehler beim Laden der DokumentStatus", err),
    // nur abfragen, wenn Dokumente zum Prüfen vorliegen
    enabled: docs.some(d => d.dateiName !== "" && d.documentId !== "" && d.subject !== "" && d.status === DefenderDokumentStatus.NotChecked),
  });
};
